import { useSuspenseQuery } from '@tanstack/react-query';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { fetchRecentClearsExistenceProbe } from '@/enterprise/data/useRecentClearsProbe';
import useTakerHistory from '@/enterprise/data/useTakerHistory';
import useTakerHistoryStats from '@/enterprise/data/useTakerHistoryStats';
import { ConsolidatedHistoryUrlState } from '@/enterprise/pages/ConsolidatedHistory';

interface ConsolidatedHistoryParams extends ConsolidatedHistoryUrlState {
  makerOrganizationUuids: string[];
}

const useConsolidatedHistoryData = (params: ConsolidatedHistoryParams) => {
  const { selectedCurrency: currency } = useCurrencyContext();

  const endDateForHistory = params.includeFuturePayDates ? '' : params.endDate;

  // The dates sent in the request are processed as deposit dates
  const dateType = 'depositDate';

  // Table data is called without a toCurrency parameter so the clear amounts are returned in the original currency
  // pendingClear is not filtered so both awarded and pending rows are returned in the combined view
  const { data: tableData = [], isFetching: isFetchingTableData } = useTakerHistory({
    ...params,
    makerOrganizationUuids: params.makerOrganizationUuids,
    endDate: endDateForHistory,
    dateType,
  });

  const hasNoTableData = tableData.length === 0;

  const { data: allTimeData } = useSuspenseQuery({
    queryKey: ['recent-clears-existence-probe', 'consolidated', params.makerOrganizationUuids],
    queryFn: () =>
      hasNoTableData ? fetchRecentClearsExistenceProbe({ makerOrganizationUuids: params.makerOrganizationUuids }) : [],
  });

  const hasHistoryOutsideRange = hasNoTableData && (allTimeData?.length ?? 0) > 0;

  const { data: chartData, isFetching: isFetchingChartData } = useTakerHistory({
    currency,
    endDate: endDateForHistory,
    makerOrganizationUuids: params.makerOrganizationUuids,
    limit: 10000, // increase limit for chart data
    pendingClear: false,
    sort: '-depositDate',
    startDate: params.startDate,
    dateType,
  });

  const { data: stats, isFetching: isFetchingStats } = useTakerHistoryStats({
    currency,
    endDate: endDateForHistory,
    excludePending: true,
    makerOrganizationUuids: params.makerOrganizationUuids,
    startDate: params.startDate,
    dateType,
  });

  // In SEA, stats endpoint return the latest clear pay date instead of making another API call
  const latestPayDate = stats?.latestClearPayDate ?? undefined;
  const latestDepositDate = stats?.latestClearDepositDate ?? undefined;

  return {
    data: {
      chartData,
      hasHistoryOutsideRange,
      latestPayDate,
      latestDepositDate,
      stats,
      tableData: tableData ?? [],
    },
    isFetchingChartData,
    isFetchingLatestPayDate: isFetchingStats,
    isFetchingLatestDepositDate: isFetchingStats,
    isFetchingStats,
    isFetchingTableData,
  };
};

export default useConsolidatedHistoryData;
